// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sharedDateTimeOptions } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useContext } from "react";
import * as React from "react";

const enum fields {
    show = "show",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.show]: string;
    };
}

export interface OrderDetailsShipmentsDataStyles {
    titleText?: TypographyPresentationProps;
    data?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const orderDetailsShipmentsDataStyles: OrderDetailsShipmentsDataStyles = {};
const styles = orderDetailsShipmentsDataStyles;

type Props = OwnProps;

const OrderDetailsShipmentsData = ({ fields }: Props) => {
    const { value: order } = useContext(OrderStateContext);

    if (!order || !order.properties.shipmentsData) {
        return null;
    }

    const shipmentData = JSON.parse(order.properties.shipmentsData);
    const shipments = Object.keys(shipmentData).map(key => shipmentData[key]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.show === "SalesOrderNumber" && (
                <>
                    <Typography as="h3" {...styles.titleText}>
                        {translate("Sales Order #")}
                    </Typography>
                    {shipments.map((shipment, index) => (
                        <Typography {...styles.data} key={index}>
                            {shipment.SalesOrderNumber}
                        </Typography>
                    ))}
                </>
            )}
            {fields.show === "DateShipped" && (
                <>
                    <Typography as="h3" {...styles.titleText}>
                        {translate("Shipment Date")}
                    </Typography>
                    {shipments.map((shipment, index) => (
                        <Typography {...styles.data} key={index}>
                            <LocalizedDateTime
                                dateTime={shipment.DateShipped}
                                options={sharedDateTimeOptions}
                            />
                        </Typography>
                    ))}
                </>
            )}
            {fields.show === "DeliveryNumber" && (
                <>
                    <Typography as="h3" {...styles.titleText}>
                        {translate("Delivery #")}
                    </Typography>
                    {shipments.map((shipment, index) => (
                        <Typography {...styles.data} key={index}>
                            {shipment.DeliveryNumber}
                        </Typography>
                    ))}
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsShipmentsData,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.show,
                displayName: "Show",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Show Sales Order Number", value: "SalesOrderNumber" },
                    { displayName: "Show Delivery Number", value: "DeliveryNumber" },
                    { displayName: "Show Date Shipped", value: "DateShipped" },
                ],
                fieldType: "General",
                defaultValue: "showAll",
            },
        ],
    },
};

export default widgetModule;
